// A farm is three cultivated hexes, with independent crop meshes for the live
// growing/harvesting cycle. Static thumbnails use exactly the same architecture.
import type { BufferGeometry } from 'three'
import { MeshBuilder } from './mesh'
import { MAT, rng3d } from './palette3d'

export const FARM_FIELDS = [
  { x: 0, z: 0 }, { x: Math.sqrt(3) * 24, z: 0 }, { x: Math.sqrt(3) * 12, z: 36 },
] as const
export const FARM_SOIL_Y = 3.2
const SOIL = '#705039', RIDGE = '#886045', EDGE = '#ad8860'
const seedKey = (seed: number) => seed & 3

/** Pointy hex, with outward-facing top and sides (MeshBuilder.cyl is flat-top). */
function bed(mb: MeshBuilder, x: number, z: number, radius: number, bottom: number, top: number, color: string): void {
  for (let side = 0; side < 6; side++) {
    const a = Math.PI / 6 + side * Math.PI / 3, b = a + Math.PI / 3
    const ax = x + Math.cos(a) * radius, az = z + Math.sin(a) * radius
    const bx = x + Math.cos(b) * radius, bz = z + Math.sin(b) * radius
    mb.tri(x, top, z, bx, top, bz, ax, top, az, color)
    mb.quad([ax, top, az], [bx, top, bz], [bx, bottom, bz], [ax, bottom, az], color, .75 + (side % 2) * .08)
  }
}

function fence(mb: MeshBuilder, ax: number, az: number, bx: number, bz: number): void {
  for (const [x, z] of [[ax, az], [bx, bz]]) {
    mb.box(x, 4.1, z, 1.1, 8.2, 1.1, MAT.timber)
    mb.pyramid(x, 8.2, z, .8, 1.1, 4, MAT.woodPale, Math.PI / 4)
  }
  for (const y of [3.8, 6.4]) mb.beam([ax, y, az], [bx, y, bz], .75, MAT.woodPale)
}

/** Fixed soil, farmyard, shelter and small details; no standing crops. */
export function farmBase(mb: MeshBuilder, color: string, seed: number): void {
  const rand = rng3d(seedKey(seed) + 302)
  for (const { x, z } of FARM_FIELDS) {
    bed(mb, x, z, 23, .15, .9, EDGE)
    bed(mb, x, z, 21.6, .9, FARM_SOIL_Y - .32, SOIL)
    // Length follows the sloping hex boundary: ploughed earth never becomes a
    // square protruding into the neighboring terrain. Narrow gaps read as furrows.
    for (let row = -3; row <= 3; row++) {
      const dx = row * 4.8, halfLength = 20.2 - (Math.abs(dx) + 1.7) / Math.sqrt(3)
      mb.box(x + dx, FARM_SOIL_Y - .16, z, 3.4, .32, halfLength * 2, RIDGE)
      mb.box(x + dx - 1.25, FARM_SOIL_Y - .04, z, .33, .08, halfLength * 2 - .6, '#98704d')
    }
    // A few earth clods make the cultivated edge less mechanically perfect.
    for (const side of [-1, 1]) {
      const dx = side * 17.8, dz = (rand() - .5) * 12
      mb.gem(x + dx, 3, z + dz, 1.5, .9, 1.3, SOIL, 4)
    }
  }
  // The sheltered tool corner is deliberately inside the anchor hex. Its open
  // face and a dark doorway remain legible when viewed from the game camera.
  mb.box(-7.5, 3.7, -9.5, 14, 1, 10.8, MAT.woodDk)
  mb.box(-7.5, 8, -13.7, 12, 8, 1.1, MAT.wood)
  mb.box(-13.1, 8, -9.5, 1.1, 8, 8.2, MAT.wood)
  for (const x of [-13.1, -1.9]) for (const z of [-13.7, -5.3]) mb.box(x, 8, z, 1, 9.6, 1, MAT.timber)
  mb.gable(-7.5, 12.8, -9.5, 16, 5.4, 11, MAT.thatch)
  mb.box(-7.5, 18.1, -9.5, 1.1, 1.1, 12.5, MAT.thatchDk)
  for (const x of [-11.6, -7.5, -3.4]) mb.beam([x, 12.9, -15.6], [-7.5, 18.1, -15.6], .45, MAT.thatchDk)
  mb.box(-7.5, 12, -4.85, 4.5, 2, .45, color) // village-colored lintel
  // Working tools, a low seed sack and a little open harvest basket.
  mb.beam([-12, 4.2, -7.3], [-10.8, 12.4, -9.5], .55, MAT.woodPale)
  mb.box(-10.8, 12.5, -9.5, 3.4, .7, 1.1, MAT.metalDk)
  mb.beam([-9.4, 4.2, -7.2], [-8.5, 11.8, -9.6], .55, MAT.woodPale)
  mb.box(-8.5, 12, -9.6, 2.2, 1.6, .55, MAT.woodDk)
  mb.gem(-3.6, 6.2, -11.4, 4.3, 4.6, 3.5, MAT.cloth, 5)
  mb.box(-3.6, 8.3, -11.4, 1.2, .8, 1.1, MAT.woodDk)
  basket(mb, 1.4, FARM_SOIL_Y, -10.8)
  // Short outer fence sections leave a broad, obvious working entrance. None
  // of the rails bridge an unowned hex or wall off the center work area.
  fence(mb, -17.7, -8, -17.7, 8)
  fence(mb, -15.5, 11.7, -5.5, 17.4)
  fence(mb, 6.8, 16.8, 16.4, 11.3)
  fence(mb, FARM_FIELDS[1].x + 17.7, -8, FARM_FIELDS[1].x + 17.7, 8)
  fence(mb, FARM_FIELDS[2].x - 12.5, 50.2, FARM_FIELDS[2].x, 57.4)
  // A familiar, cheerful silhouette over the back plot instead of more tiny
  // props: timber arms, patched shirt, cloth head and wide straw hat.
  const sx = FARM_FIELDS[2].x + 10.8, sz = 36
  mb.box(sx, 9.8, sz, .9, 13.2, .9, MAT.woodDk)
  mb.box(sx, 12.3, sz, 9.2, .8, .8, MAT.woodPale)
  mb.box(sx, 11.5, sz, 3.9, 4.5, 2, color)
  mb.box(sx + 1, 11, sz - 1.1, 1.2, 1.5, .15, MAT.cloth)
  mb.gem(sx, 15.2, sz, 2.5, 3, 2.3, MAT.cloth, 4)
  mb.cyl(sx, 16.4, sz, 3, .6, 6, MAT.thatchDk)
  mb.pyramid(sx, 17, sz, 1.8, 2.4, 6, MAT.thatch)
}

function basket(mb: MeshBuilder, x: number, y: number, z: number): void {
  mb.cyl(x, y, z, 2.8, 2.5, 8, MAT.woodDk, false)
  mb.cyl(x, y + 2.3, z, 3, .55, 8, MAT.woodPale, false)
  mb.cyl(x, y + .25, z, 2.5, .15, 8, SOIL)
  for (const side of [-1, 1]) mb.beam([x + side * 2.6, y + 2.5, z], [x + side * 2.6, y + 4, z], .45, MAT.woodPale)
}

/** Eight triangles give an ear of grain a pointed, faceted silhouette. */
function grain(mb: MeshBuilder, x: number, y: number, z: number, h: number, color: string): void {
  const radius = .68
  for (let side = 0; side < 4; side++) {
    const a = side * Math.PI / 2, b = a + Math.PI / 2
    const ax = x + Math.cos(a) * radius, az = z + Math.sin(a) * radius
    const bx = x + Math.cos(b) * radius, bz = z + Math.sin(b) * radius
    mb.tri(x, y + h, z, bx, y + h * .4, bz, ax, y + h * .4, az, color, .86 + side * .04)
    mb.tri(x, y, z, ax, y + h * .4, az, bx, y + h * .4, bz, color, .82)
  }
}

function leaf(mb: MeshBuilder, x: number, y: number, z: number, dx: number, dz: number, h: number, color: string): void {
  const ax = x - dz * .22, az = z + dx * .22
  const bx = x + dz * .22, bz = z - dx * .22
  mb.tri(ax, y, az, x + dx, y + h, z + dz, bx, y, bz, color)
  mb.tri(bx, y, bz, x + dx, y + h, z + dz, ax, y, az, color, .83)
}

/** One field at the origin, standing on y=0 for simple growth/sway transforms. */
export function farmCrops(mb: MeshBuilder, stage: 0 | 1 | 2, seed: number, field: number): void {
  const plot = Math.max(0, Math.min(2, field | 0)), rand = rng3d(seedKey(seed) * 17 + plot * 311 + 904)
  for (let row = -3; row <= 3; row++) for (let column = -4; column <= 4; column++) {
    const x = row * 4.8 + (rand() - .5) * .7, z = column * 4.15 + (row % 2) * .75 + (rand() - .5) * .65
    // Reserve a farmyard around the shed and basket. Keep all leaves and grain
    // inside the cultivated hex, including at the pointed front and back ends.
    if (Math.abs(x) + Math.sqrt(3) * Math.abs(z) > Math.sqrt(3) * 18.5 || (plot === 0 && x < 5 && z < -2.5)) continue
    const variation = rand(), green = variation > .5 ? '#88a749' : '#a3b855', gold = variation > .45 ? '#e3bd58' : '#d2a54b'
    if (stage === 0) {
      leaf(mb, x, 0, z, 1.1, .5, 1.7 + variation, green)
      leaf(mb, x, 0, z, -.9, -.6, 1.5 + variation, '#729747')
      continue
    }
    const height = (stage === 1 ? 3.3 : 5) + variation * 1.2, stem = stage === 1 ? '#839d42' : '#c19c44'
    mb.box(x, height / 2, z, .28, height, .28, stem)
    leaf(mb, x, height * .38, z, 1.05, .65, 1.4, stage === 1 ? green : '#cdb051')
    leaf(mb, x, height * .25, z, -.8, -.75, 1.15, stage === 1 ? '#688940' : '#b3903e')
    grain(mb, x, height - .25, z, stage === 1 ? 1.3 : 2.5, stage === 1 ? '#b5c36a' : gold)
  }
}

/** Full, mature static model for build cards, ghosts and the model gallery. */
export function farmArchitecture(mb: MeshBuilder, color: string, seed = 0): void {
  farmBase(mb, color, seed)
  for (let field = 0; field < FARM_FIELDS.length; field++) {
    const crops = new MeshBuilder(), { x, z } = FARM_FIELDS[field]
    farmCrops(crops, 2, seed, field)
    mb.merge(crops, x, FARM_SOIL_Y, z)
  }
}

const baseCache = new Map<string, BufferGeometry>(), cropCache = new Map<string, BufferGeometry>()
let harvest: BufferGeometry | undefined
export function farmBaseGeometry(color: string, seed: number): BufferGeometry {
  const key = `${color}:${seedKey(seed)}`
  let geometry = baseCache.get(key)
  if (!geometry) {
    const mb = new MeshBuilder(); farmBase(mb, color, seed)
    geometry = mb.geometry(); baseCache.set(key, geometry)
  }
  return geometry
}
export function farmCropGeometry(stage: 0 | 1 | 2, seed: number, field: number): BufferGeometry {
  const phase = Math.max(0, Math.min(2, stage | 0)) as 0 | 1 | 2, plot = Math.max(0, Math.min(2, field | 0))
  const key = `${phase}:${seedKey(seed)}:${plot}`
  let geometry = cropCache.get(key)
  if (!geometry) {
    const mb = new MeshBuilder(); farmCrops(mb, phase, seed, plot)
    geometry = mb.geometry(); cropCache.set(key, geometry)
  }
  return geometry
}
/** Small grain pile fits the fixed basket at (1.4, soil+2.5, -10.8). */
export function farmHarvestGeometry(): BufferGeometry {
  if (!harvest) {
    const mb = new MeshBuilder()
    mb.gem(0, .25, 0, 4.6, 1.7, 4.6, MAT.thatch, 6)
    for (const [x, z] of [[-1, -.7], [.5, -.9], [1.3, .5], [-.8, 1.1], [0, 0]]) grain(mb, x, .7, z, 1.2, '#e1bd62')
    harvest = mb.geometry()
  }
  return harvest
}
